/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */

export type Outcome = 'success' | 'failed' | 'aborted' | 'timeout' | 'occupied' | 'redirecting';

export const LOA_POPUP_WINDOW_NAME = 'loa-elevation';

export interface OpenSecuritySetupOptions {
  loaLevel: number;
  timeoutMs?: number;
  origin?: string;
  path?: string;
  width?: number;
  height?: number;
  useNoopener?: boolean;

  applicationId?: string; // required for redirect fallback
  fallback?: 'same-tab' | 'new-tab'; // redirect fallback target
  dropPathSegments?: number; // default 1 (drop "appName")
  instance?: string;
  redirectParams?: Record<string, string>; // extra query params appended to relativePath
  resourceName?: string; // resource name appended on the security setup page to provide additional context for the user
  onPopupOpened?: () => void;
}

let inflight = false;

type BuildUrlArgs = Required<Pick<OpenSecuritySetupOptions, 'loaLevel' | 'origin' | 'path'>> & {
  channelId: string;
  mode: 'popup' | 'redirect';
  applicationId?: string;
  relativePath?: string;
  instance?: string;
  resourceName?: string;
};

function buildUrl(opts: BuildUrlArgs): string {
  const {
    loaLevel, origin, path, channelId, mode, applicationId, relativePath, instance,
    resourceName
  } = opts;
  const url = new URL(path, origin);
  url.searchParams.set('loaLevel', String(loaLevel));
  url.searchParams.set('channelId', channelId);
  url.searchParams.set('mode', mode);
  if (instance) url.searchParams.set('instance', instance);
  if (resourceName) url.searchParams.set('resourceName', resourceName);

  if (mode === 'redirect') {
    if (applicationId) url.searchParams.set('applicationId', applicationId);
    if (relativePath) url.searchParams.set('relativePath', encodeURIComponent(relativePath));
  }

  return url.toString();
}

function dropSegments(pathname: string, count: number): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length <= count) return '/';
  return `/${parts.slice(count).join('/')}`;
}

function buildRelativePath(
  dropCount: number,
  extraParams?: Record<string, string>
): string {
  const { pathname, search, hash } = window.location;
  const relPath = dropSegments(pathname, dropCount);
  const params = new URLSearchParams(search);
  if (extraParams) {
    Object.entries(extraParams)
      .forEach(([key, value]) => { if (value !== undefined && value !== null) params.set(key, value); });
  }
  const queryString = params.toString();
  return `${relPath}${queryString ? `?${queryString}` : ''}${hash || ''}`;
}

function featuresCenteredOnOpener(w = 640, h = 700) {
  const x = (window.screenX ?? (window as any).screenLeft ?? 0);
  const y = (window.screenY ?? (window as any).screenTop ?? 0);
  const outerW = (window.outerWidth ?? document.documentElement.clientWidth ?? 0);
  const outerH = (window.outerHeight ?? document.documentElement.clientHeight ?? 0);

  const left = Math.round(x + (outerW - w) / 2);
  const top = Math.round(y + (outerH - h) / 2);

  return `popup=yes,width=${w},height=${h},left=${left},top=${top}`;
}

function isProbablyBlockedPopup(popup: Window | null): boolean {
  if (!popup) return true;

  try {
    if (popup.closed) return true;
  } catch {
    return true;
  }

  return false;
}

function waitForOutcome(
  channelId: string,
  timeoutMs: number,
  popup: Window | null
): Promise<Outcome> {
  return new Promise<Outcome>((resolve) => {
    if (typeof window.BroadcastChannel !== 'function') {
      console.warn('[LoA] BroadcastChannel not supported in this browser');
      resolve('failed');
      return;
    }

    const baseName = `loa-upgrade:${channelId}`;
    const ch = new BroadcastChannel(baseName);

    let currentNavId: string | null = null;
    let timeoutHandle: number | null = null;
    let abortedDelayHandle: number | null = null;
    let pollingHandle: number | null = null;

    const ABORT_DEBOUNCE_MS = 2000;
    const POPUP_POLL_MS = 500;

    const cancelAbort = () => {
      if (abortedDelayHandle) {
        clearTimeout(abortedDelayHandle);
        abortedDelayHandle = null;
      }
    };

    // A LOA_ABORTED message fires on *every* popup document unload — including a
    // reload or an intra-popup navigation, not just a genuine user cancel. So we
    // never trust it on its own: after the debounce we only resolve 'aborted' if
    // the popup window is actually gone (popup.closed). If it is still open the
    // ABORTED came from a reload/navigation — drop it and keep waiting (the
    // popup.closed poll and the overall timeout remain the real safety nets).
    // When we have no window handle (redirect flow / blocked popup) we cannot
    // verify, so we fall back to trusting the debounced message.
    const scheduleAbort = () => {
      if (abortedDelayHandle != null) return;
      abortedDelayHandle = window.setTimeout(() => {
        abortedDelayHandle = null;
        if (popup) {
          let stillOpen = true;
          try { stillOpen = !popup.closed; } catch { stillOpen = true; }
          if (stillOpen) return;
        }
        cleanup('aborted');
      }, ABORT_DEBOUNCE_MS);
    };

    const cleanup = (outcome: Outcome) => {
      cancelAbort();
      if (pollingHandle) { clearInterval(pollingHandle); pollingHandle = null; }
      try { ch.close(); } catch { /* noop */ }
      if (timeoutHandle) clearTimeout(timeoutHandle);
      try { if (popup && !popup.closed && outcome !== 'success') popup.close(); } catch { /* noop */ }
      resolve(outcome);
    };

    ch.onmessage = (ev: MessageEvent) => {
      const message = ev?.data;
      if (!message || typeof message.type !== 'string') return;

      if (message.type === 'LOA_READY' && typeof message.navId === 'string') {
        cancelAbort();
        if (currentNavId !== message.navId) {
          currentNavId = message.navId;
        }
        return;
      }

      if (!currentNavId || message.navId !== currentNavId) return;

      if (message.type === 'LOA_ELEVATED') return cleanup('success');
      if (message.type === 'LOA_FAILED') return cleanup('failed');
      if (message.type === 'LOA_ABORTED') return scheduleAbort();
    };

    // Poll popup.closed to detect cross-origin closes where BC messages cannot arrive.
    // Unlike BC LOA_ABORTED (which fires on every intra-popup navigation), popup.closed
    // is definitive — the window is gone — so we skip the debounce and clean up immediately.
    if (popup) {
      pollingHandle = window.setInterval(() => {
        try {
          if (popup.closed) {
            cleanup('aborted');
          }
        } catch { /* cross-origin access error — popup still alive, ignore */ }
      }, POPUP_POLL_MS);
    }

    if (timeoutMs > 0) {
      timeoutHandle = window.setTimeout(() => cleanup('timeout'), timeoutMs);
    }
  });
}

function startRedirectFallback(args: {
  loaLevel: number;
  origin: string;
  path: string;
  channelId: string;
  applicationId: string;
  dropCount: number;
  fallback: 'same-tab' | 'new-tab';
  instance?: string;
  redirectParams?: Record<string, string>;
  resourceName?: string;
}): void {
  const {
    loaLevel, origin, path, channelId, applicationId, dropCount, fallback, instance,
    redirectParams, resourceName
  } = args;

  const relativePath = buildRelativePath(dropCount, redirectParams);

  const redirectUrl = buildUrl({
    loaLevel,
    origin,
    path,
    channelId,
    mode: 'redirect',
    applicationId,
    relativePath,
    instance,
    resourceName
  });

  if (fallback === 'new-tab') {
    window.open(redirectUrl, '_blank', 'noopener');
  } else {
    window.location.assign(redirectUrl);
  }
}

export async function openSecuritySetup(options: OpenSecuritySetupOptions): Promise<Outcome> {
  if (inflight) return 'occupied';
  inflight = true;

  const {
    loaLevel,
    timeoutMs = 10 * 60 * 1000, // default 10 minutes
    origin = window.location.origin,
    path = '/platform-apps/loa/securitysetup',
    width = 640,
    height = 700,
    useNoopener = false,

    applicationId,
    fallback = 'same-tab',
    dropPathSegments: dropCount = 1,
    instance = crypto.randomUUID(),
    redirectParams,
    resourceName
  } = options;

  // If BroadcastChannel is not supported, the popup cannot communicate back.
  // Fall back to redirect flow (same-tab navigation) instead of opening an orphaned popup.
  if (typeof window.BroadcastChannel !== 'function') {
    console.warn('[LoA] BroadcastChannel not supported — using redirect fallback');
    try {
      if (!applicationId) return 'failed';
      const fallbackChannelId = crypto.randomUUID();
      startRedirectFallback({
        loaLevel,
        origin,
        path,
        channelId: fallbackChannelId,
        applicationId,
        dropCount,
        fallback,
        instance,
        redirectParams,
        resourceName
      });
      return 'redirecting'; // page navigates away for same-tab; return is a safeguard
    } finally {
      inflight = false;
    }
  }

  const channelId = crypto.randomUUID();
  const winName = LOA_POPUP_WINDOW_NAME;
  const windowOptions = featuresCenteredOnOpener(width, height) + (useNoopener ? ',noopener' : '');

  const popupUrl = buildUrl({
    loaLevel,
    origin,
    path,
    channelId,
    mode: 'popup',
    instance,
    resourceName
  });

  const popup = window.open(popupUrl, winName, windowOptions);

  if (!isProbablyBlockedPopup(popup)) {
    options.onPopupOpened?.();
  }

  if (isProbablyBlockedPopup(popup)) {
    try {
      if (!applicationId) return 'failed';
      startRedirectFallback({
        loaLevel,
        origin,
        path,
        channelId,
        applicationId,
        dropCount,
        fallback,
        instance,
        redirectParams,
        resourceName
      });
      return 'redirecting';
    } finally {
      inflight = false;
    }
  }

  try {
    return await waitForOutcome(channelId, timeoutMs, popup);
  } finally {
    inflight = false;
  }
}
